<template>
  <span class="inline-flex items-center" :class="labelGapClass">
    <span
      role="img"
      :aria-label="`${levelName} level`"
      class="relative block flex-none"
      :class="level === 'lightkeeper' ? 'rounded-md bg-burgundy-900' : ''"
      :style="boxStyle"
    >
      <!-- The Fyr mark's three shapes, from assets/fyr-logo.svg. -->
      <svg
        viewBox="62 34 210 220"
        class="size-full"
        aria-hidden="true"
        focusable="false"
      >
        <path
          :class="partClass(3)"
          d="M142.4,249.6H80c-7.4,0-13.4-6-13.4-13.4V52.1c0-7.4,6-13.4,13.4-13.4h62.4c7.4,0,13.4,6,13.4,13.4v184.1C155.8,243.6,149.8,249.6,142.4,249.6z"
        />
        <path
          :class="partClass(2)"
          d="M254.2,112.8h-72.5c-7.4,0-13.4-6-13.4-13.4V51.8c0-7.4,6-13.4,13.4-13.4h72.5c7.4,0,13.4,6,13.4,13.4v47.6C267.6,106.8,261.6,112.8,254.2,112.8z"
        />
        <path
          :class="partClass(1)"
          d="M198.2,186.5L198.2,186.5c-17.9,0-32.4-14.5-32.4-32.4v0c0-17.9,14.5-32.4,32.4-32.4h0c17.9,0,32.4,14.5,32.4,32.4v0C230.6,172,216.1,186.5,198.2,186.5z"
        />
      </svg>
    </span>
    <span
      v-if="showLabel"
      :id="labelId"
      aria-hidden="true"
      class="leading-none font-semibold whitespace-nowrap"
      :class="labelClass"
    >
      {{ levelName }}
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed, PropType } from "vue";
import { LEVEL_COPY } from "@/copy/setupProgress";
import type { SetupLevel } from "@/services/setup_progress";

const props = defineProps({
  level: { type: String as PropType<SetupLevel>, required: true },
  size: { type: String as PropType<"sm" | "md" | "lg">, default: "md" },
  showLabel: { type: Boolean, default: true },
  labelId: { type: String, default: undefined },
});

const LIT_PARTS: Record<SetupLevel, number> = {
  explorer: 1,
  wayfinder: 2,
  navigator: 3,
  lightkeeper: 3,
};

const box = computed(() => ({ sm: 16, md: 24, lg: 44 })[props.size]);
const TILE = {
  sm: { grow: 4, padding: 3 },
  md: { grow: 4, padding: 4 },
  lg: { grow: 6, padding: 7 },
};
// Every badge of a size takes the tile's box, so labels beside them line up.
const boxStyle = computed(() => {
  const { grow, padding } = TILE[props.size];
  const side = `${box.value + grow}px`;
  return {
    width: side,
    height: side,
    padding: `${props.level === "lightkeeper" ? padding : grow / 2}px`,
  };
});
const levelName = computed(() => LEVEL_COPY[props.level].name);
const labelGapClass = computed(
  () => ({ sm: "gap-1.5", md: "gap-2", lg: "gap-3" })[props.size],
);
const labelClass = computed(
  () =>
    ({
      sm: "text-xs",
      md: "text-sm",
      lg: "font-artex text-2xl text-burgundy-900",
    })[props.size],
);

function partClass(order: number): string {
  if (props.level === "lightkeeper") {
    return order === 1 ? "fill-purple-400" : "fill-white";
  }
  return LIT_PARTS[props.level] >= order
    ? "fill-purple-800"
    : "fill-purple-200";
}
</script>
